import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { CheckIcon, ColumnPinIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

import type { DataTablePin } from './data-table-types'

interface ColumnPinPopoverProps {
  currentPin: DataTablePin | undefined
  onChange: (pin: DataTablePin | null) => void
}

function PinOption({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex select-none items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100',
        active && 'text-primary',
      )}
    >
      <span>{label}</span>
      {active && <CheckIcon className="size-2.5" />}
    </button>
  )
}

function PinTrigger({
  isPinned,
  open,
  ariaLabel,
}: {
  isPinned: boolean
  open: boolean
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={isPinned}
      className={cn(
        'inline-flex select-none items-center justify-center rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100',
        (isPinned || open) && 'text-primary opacity-100',
      )}
    >
      <ColumnPinIcon className="size-3.5" />
    </button>
  )
}

export function ColumnPinPopover({
  currentPin,
  onChange,
}: ColumnPinPopoverProps) {
  const { t } = useTranslation()
  const ariaLabel = t('table.pin.toggle')

  return (
    <Popover
      portal
      align="right"
      side="bottom"
      contentClassName="min-w-32"
      trigger={(open) => (
        <PinTrigger
          isPinned={Boolean(currentPin)}
          open={open}
          ariaLabel={ariaLabel}
        />
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-0.5 rounded-md border border-gray-200 bg-white p-1 shadow-md">
          <PinOption
            label={t('table.pin.left')}
            active={currentPin === 'left'}
            onClick={() => {
              onChange('left')
              close()
            }}
          />
          <PinOption
            label={t('table.pin.right')}
            active={currentPin === 'right'}
            onClick={() => {
              onChange('right')
              close()
            }}
          />
          {currentPin && (
            <PinOption
              label={t('table.pin.unpin')}
              active={false}
              onClick={() => {
                onChange(null)
                close()
              }}
            />
          )}
        </div>
      )}
    </Popover>
  )
}
